import { useState } from "react";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import NodeAPI from "../api"; // adjust the path if needed
import { Box, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link, useNavigate } from "react-router-dom";
// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;



const QuizUploadPage = () => {
  const [inputText, setInputText] = useState("");
  const [questions, setQuestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (route) => {
    setAnchorEl(null);
    if (route) navigate(route);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    navigate("/login");
  };

  const handleTextChange = (e) => setInputText(e.target.value);

  const normalizeText = (rawText) =>
    rawText
      .replace(/\r\n|\r/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{2,}/g, "\n\n")
      .trim();

const parseQuestions = (text) => {
  const mcqPattern =
    /(\d+)\.\s*(.+?)\nA[.)]?\s*(.+?)\nB[.)]?\s*(.+?)\nC[.)]?\s*(.+?)\nD[.)]?\s*(.+?)\n(?:Answer|Ans|Correct Answer)[:\-]?\s*(.+)/g;

  const structuredPattern =
    /(\d+)\.\s*(.+?)\nAnswer[:\-]?\s*(.*?)(?=\n\d+\.|\n*$)/gs;

  const mcqMatches = [...text.matchAll(mcqPattern)];

  if (mcqMatches.length > 0) {
    return mcqMatches.map((match) => {
      const question = match[2].trim();
      const options = [
        match[3].trim(),
        match[4].trim(),
        match[5].trim(),
        match[6].trim(),
      ];

      let answer = match[7].trim();
      const letterIndex = "ABCD".indexOf(answer.toUpperCase());
      if (letterIndex >= 0) {
        answer = options[letterIndex];
      }

      return {
        type: "mcq",
        question,
        options,
        answer,
      };
    });
  }

  // Try structured questions if no MCQ matches
  const structuredMatches = [...text.matchAll(structuredPattern)];

  /*return structuredMatches.map((match) => {
    return {
      type: "structured",
      question: match[2].trim(),
      answer: match[3].trim(),
    };
  });*/

return structuredMatches.map((match) => ({
  type: "structured",
  question: match[2].trim(),
  options: [], // ensure it exists
  answer: match[3].trim(),
}));


};

const handleExtract = () => setQuestions(parseQuestions(inputText));


  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();

    if (file.type === "application/pdf") {
      const pdfjsLib = await import("pdfjs-dist/build/pdf");
      const pdfWorker = await import("pdfjs-dist/build/pdf.worker.entry");
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker.default;
      reader.onload = async () => {
        const typedarray = new Uint8Array(reader.result);
        const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
        let text = "";
        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const content = await page.getTextContent();
          text += content.items.map((item) => item.str).join("\n") + "\n\n";
        }
        setInputText(normalizeText(text));
        setUploading(false);
      };
      reader.readAsArrayBuffer(file);
    } else if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      reader.onload = async () => {
        const result = await mammoth.extractRawText({
          arrayBuffer: reader.result,
        });
        setInputText(normalizeText(result.value));
        setUploading(false);
      };
      reader.readAsArrayBuffer(file);
    } else if (file.type.startsWith("text")) {
      reader.onload = () => {
        setInputText(normalizeText(reader.result));
        setUploading(false);
      };
      reader.readAsText(file);
    } else {
      alert("Unsupported file type");
      setUploading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    Tesseract.recognize(file, "eng")
      .then(({ data: { text } }) => {
        setInputText(normalizeText(text));
        setUploading(false);
      })
      .catch(() => setUploading(false));
  };

  const handleCombinedUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileType = file.type;
    if (fileType.startsWith("image/")) {
      handleImageUpload(event);
    } else {
      handleFileUpload(event);
    }
  };

  const handleSpeech = () => {
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputText((prev) => prev + "\n" + transcript);
    };
    recognition.onerror = (e) => console.error("Speech error:", e);
    recognition.start();
  };

  const handleChangeQuestion = (index, key, value) => {
    const updated = [...questions];
    if (key === "option") {
      updated[index].options = value;
    } else {
      updated[index][key] = value;
    }
    setQuestions(updated);
  };

  const handleSave = async () => {
    const hasMCQ = questions.some((q) => q.options?.length >= 2);
    const hasStructured = questions.some((q) => !q.options || q.options.length < 2);

    if (hasMCQ && hasStructured) {
      return alert("You cannot mix Multiple Choice and Structured questions in the same quiz.");
    }

    const valid = questions.every(
      (q) =>
        q.question &&
        (q.options?.length >= 2 ? q.options.includes(q.answer) : true)
    );
    if (!valid) return alert("Some questions are incomplete or invalid");

    const topic = prompt("Enter a topic name:");
    const category = "custom_quiz";
    const userId = localStorage.getItem("userId");

    if (!topic || !category || !userId) {
      return alert("Missing topic name or user ID.");
    }

    try {
      await NodeAPI.post("/quiz/save", { topic, category, questions, userId });
      alert("✅ Quiz saved successfully!");
      setQuestions([]);
      setInputText("");
    } catch (error) {
      console.error("❌ Save failed:", error);
      alert(`Save failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const styles = {
    container: { padding: "20px", fontFamily: "Arial, sans-serif" },
    heading: { fontSize: "24px", fontWeight: "bold", marginBottom: "10px" },
    textarea: { width: "100%", height: "150px", marginBottom: "10px" },
    input: { margin: "5px 10px 5px 0" },
    button: { margin: "5px 10px 5px 0", padding: "5px 10px" },
    sectionTitle: { fontSize: "20px", marginTop: "20px" },
    card: { margin: "10px 0", padding: "10px", border: "1px solid #ccc" },
    inputFull: { width: "100%", margin: "5px 0" },
    select: { width: "100%", padding: "5px", marginTop: "5px" },
    saveButton: {
      padding: "10px 20px",
      backgroundColor: "green",
      color: "white",
      border: "none",
    },
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 2,
          pt: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Quiz Topics
        </Typography>
        <div>
          <IconButton
            color="primary"
            onClick={handleMenuClick}
            aria-controls="profile-menu"
            aria-haspopup="true"
            size="large"
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={() => handleMenuClose()}
          >
            <MenuItem onClick={() => handleMenuClose("/profile")}>
              Profile
            </MenuItem>
            <MenuItem onClick={() => handleMenuClose("/notifications")}>Quiz Notifications</MenuItem>
            <MenuItem onClick={() => handleMenuClose("/uploadquiz")}>
              Upload Quiz
            </MenuItem>
            <MenuItem onClick={() => handleMenuClose("/generate_quiz")}>Generate Quiz</MenuItem>
            <MenuItem onClick={() => handleMenuClose("/categories")}>Categories</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </div>
      </Box>

      <div style={styles.container}>
        <h2 style={styles.heading}>Upload or Enter Quiz Questions</h2>

        <textarea
          value={inputText}
          onChange={handleTextChange}
          style={styles.textarea}
          placeholder={`Paste or dictate questions in this format:
1. What is the capital of France?
A) Berlin
B) Madrid
C) Paris
D) Rome
Answer: C`}
        />

        <div>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt,image/*"
            onChange={handleCombinedUpload}
            style={styles.input}
          />
          <button onClick={handleSpeech} style={styles.button}>
            🎤 Speak
          </button>
          <button onClick={handleExtract} style={styles.button}>
            🔍 Extract
          </button>
        </div>

        {uploading && <p>Processing...</p>}

       <h3 style={styles.sectionTitle}>Preview & Edit</h3>
{questions.map((q, i) => (
  <div key={i} style={styles.card}>
    <input
      value={q.question}
      onChange={(e) =>
        handleChangeQuestion(i, "question", e.target.value)
      }
      placeholder="Enter question"
      style={styles.inputFull}
    />

    {/* Render options only if it's an MCQ */}
    {q.options?.length > 0 ? (
      <>
        {q.options.map((opt, j) => (
          <input
            key={j}
            value={opt}
            onChange={(e) => {
              const newOpts = [...q.options];
              newOpts[j] = e.target.value;
              handleChangeQuestion(i, "option", newOpts);
            }}
            placeholder={`Option ${String.fromCharCode(65 + j)}`}
            style={styles.inputFull}
          />
        ))}
        <select
          value={q.answer}
          onChange={(e) =>
            handleChangeQuestion(i, "answer", e.target.value)
          }
          style={styles.select}
        >
          <option value="">Select Answer</option>
          {q.options.map((opt, j) => (
            <option key={j} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </>
    ) : (
      // Structured question — just answer input
      <input
        value={q.answer}
        onChange={(e) =>
          handleChangeQuestion(i, "answer", e.target.value)
        }
        placeholder="Enter answer"
        style={styles.inputFull}
      />
    )}
  </div>
))}


        {questions.length > 0 && (
          <button onClick={handleSave} style={styles.saveButton}>
            💾 Save Quiz
          </button>
        )}
      </div>
    </Box>
  );
};

export default QuizUploadPage;